import React from 'react';
import Icon from '../../../components/AppIcon';

const DepartmentDetails = ({ department, allDepartments }) => {
  if (!department) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-12">
          <Icon name="Building2" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">
            เลือกหน่วยงานเพื่อดูรายละเอียด
          </h3>
          <p className="text-sm text-muted-foreground">
            คลิกที่หน่วยงานในโครงสร้างองค์กรเพื่อดูข้อมูลเพิ่มเติม
          </p>
        </div>
      </div>
    );
  }

  const handleCall = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  const handleEmail = (email) => {
    window.open(`mailto:${email}`, '_blank');
  };

  // Compute staff count from children if present
  const childrenByParent = React.useMemo(() => {
    const map = new Map();
    allDepartments?.forEach((d) => {
      const key = d?.parentId ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    });
    return map;
  }, [allDepartments]);

  const computeStaffCount = (dept) => {
    if (!dept) return 0;
    if (dept?.level === 3) return Number(dept?.staffCount || 0);
    const children = childrenByParent.get(dept?.id) || [];
    if (children.length === 0) return Number(dept?.staffCount || 0);
    return children.reduce((sum, child) => sum + computeStaffCount(child), 0);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      {/* Department Header */}
      <div className="flex items-start space-x-4 mb-6">
        <div className={`
          w-16 h-16 rounded-full flex items-center justify-center
          ${department?.level === 1 ? 'bg-primary text-primary-foreground' :
            department?.level === 2 ? 'bg-secondary text-secondary-foreground': 'bg-accent text-accent-foreground'}
        `}>
          <Icon name={department?.icon} size={24} />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-foreground mb-1">
            {department?.name}
          </h2>
          <p className="text-muted-foreground mb-2">
            {department?.fullName}
          </p>
          <div className="flex items-center text-sm text-muted-foreground">
            <Icon name="Users" size={16} className="mr-2" />
            จำนวนบุคลากร {computeStaffCount(department)} คน
          </div>
        </div>
      </div>
      {/* Department Head */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-foreground mb-3">
          ผู้บริหาร
        </h3>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="User" size={20} className="text-primary" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">
                {department?.head}
              </h4>
              <p className="text-sm text-muted-foreground">
                {department?.headPosition}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Responsibilities */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-foreground mb-3">
          หน้าที่ความรับผิดชอบ
        </h3>
        <ul className="space-y-2">
          {department?.responsibilities?.map((responsibility, index) => (
            <li key={index} className="flex items-start space-x-2">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
              <span className="text-sm text-foreground">{responsibility}</span>
            </li>
          ))}
        </ul>
      </div>
      {/* Services */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-foreground mb-3">
          บริการที่ให้
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {department?.services?.map((service, index) => (
            <div key={index} className="flex items-center space-x-2 p-2 bg-muted/30 rounded-md">
              <Icon name="FileText" size={14} className="text-primary flex-shrink-0" />
              <span className="text-sm text-foreground">{service}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Contact Information */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-medium text-foreground mb-4">
          ข้อมูลติดต่อ
        </h3>
        <div className="space-y-3">
          {/* Phone */}
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name="Phone" size={18} className="text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">โทรศัพท์</p>
                <p className="text-sm text-muted-foreground">{department?.contact?.phone}</p>
              </div>
            </div>
            <button
              onClick={() => handleCall(department?.contact?.phone)}
              className="px-3 py-1 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 transition-government"
            >
              โทร
            </button>
          </div>

          {/* Email */}
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name="Mail" size={18} className="text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">อีเมล</p>
                <p className="text-sm text-muted-foreground">{department?.contact?.email}</p>
              </div>
            </div>
            <button
              onClick={() => handleEmail(department?.contact?.email)}
              className="px-3 py-1 bg-secondary text-secondary-foreground rounded-md text-sm hover:bg-secondary/90 transition-government"
            >
              ส่งเมล
            </button>
          </div>

          {/* Extension */}
          <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
            <Icon name="Hash" size={18} className="text-primary" />
            <div>
              <p className="text-sm font-medium text-foreground">ต่อภายใน</p>
              <p className="text-sm text-muted-foreground">{department?.contact?.extension}</p>
            </div>
          </div>

          {/* Office Hours */}
          <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
            <Icon name="Clock" size={18} className="text-primary" />
            <div>
              <p className="text-sm font-medium text-foreground">เวลาทำการ</p>
              <p className="text-sm text-muted-foreground">{department?.contact?.hours}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentDetails;